import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { computed, action, makeObservable, observable } from "mobx";
import { AudioRecordingTransformer } from "@/store/audio/transforming/audio-recording-transformer";
import {
  AudioRecordingTransformingState,
  AudioRecordingTransformProgressTracker,
  IAudioTransformingStore,
} from "@/store/audio/transforming/audio-transforming.types";
import { Uuid } from "@/domains/global/identifiers";

export class AppStoreAudioTransformingStore
  extends AppSubStore
  implements IAudioTransformingStore, AudioRecordingTransformProgressTracker
{
  private transformer: AudioRecordingTransformer;
  private transformingStateByRecording = new Map<Uuid, AudioRecordingTransformingState>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.transformer = new AudioRecordingTransformer({
      audioRecordings: injectedDeps.store.dexie.repositories.audioRecordings,
      progressTracker: this,
      getTranscriptionStatus: (audioRecordingId: Uuid) => {
        return injectedDeps.store.audio.recording.getTranscriptionStatus(audioRecordingId);
      },
      clearTranscriptionStatus: (audioRecordingId: Uuid) => {
        return injectedDeps.store.audio.recording.clearTranscriptionStatus(audioRecordingId);
      },
      modals: injectedDeps.store.modals,
      navigation: injectedDeps.store.navigation,
    });

    makeObservable<AppStoreAudioTransformingStore, "transformer" | "transformingStateByRecording">(
      this,
      {
        transformer: observable,
        transformingStateByRecording: observable.shallow,
        debugQueuedJobs: computed,
        enqueue: action,
        setTransformState: action,
        clearTransformState: action,
        resetState: action,
        getTransformState: false,
      }
    );
  }

  get debugQueuedJobs() {
    return this.transformer.debugQueuedJobs;
  }

  async enqueue({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.transformer.enqueue({ audioRecordingId });
  }

  resetState() {
    this.transformingStateByRecording.clear();
    this.transformer.resetState();
  }

  setTransformState({
    audioRecordingId,
    state,
  }: {
    audioRecordingId: Uuid;
    state: AudioRecordingTransformingState;
  }): void {
    this.transformingStateByRecording.set(audioRecordingId, state);
  }

  clearTransformState({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.transformingStateByRecording.delete(audioRecordingId);
  }

  getTransformState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingTransformingState {
    return (
      this.transformingStateByRecording.get(audioRecordingId) ?? {
        isTransforming: false,
        errorMessage: null,
        awaitingRemoteUpdate: false,
        apiCallCompletedAt: null,
      }
    );
  }
}
